import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronDown, Info } from "lucide-react";
import { adConcepts } from "@/components/offsite-demo/data";
import { UKMap } from "@/components/offsite-demo/shared";
import { cn } from "@/lib/utils";

type Format = "square" | "portrait";

function Visual({ kind }: { kind: "tabs" | "map" | "cards" | "timeline" }) {
  if (kind === "map") {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="h-40 w-40">
          <UKMap
            dots={[
              { name: "London", count: 8 },
              { name: "Manchester", count: 3 },
              { name: "Bristol", count: 3 },
              { name: "Birmingham", count: 2 },
              { name: "Brighton", count: 2 },
              { name: "Edinburgh", count: 2 },
              { name: "Cardiff", count: 1 },
              { name: "Leeds", count: 2 },
            ]}
            meetingPoint="Birmingham"
          />
        </div>
      </div>
    );
  }

  if (kind === "tabs") {
    return (
      <div className="space-y-2">
        {["venue hire london 20 people", "train times manchester to london", "team day ideas uk", "hijingo group booking"].map(
          (t, i) => (
            <div
              key={t}
              className="rounded-lg border border-border bg-background/70 px-3 py-2 text-xs text-muted-foreground truncate"
              style={{ marginLeft: i * 8 }}
            >
              {t}
            </div>
          )
        )}
        <div className="pt-2 text-xs text-muted-foreground">+ 11 more tabs</div>
      </div>
    );
  }

  if (kind === "cards") {
    return (
      <div className="space-y-2.5">
        {[
          ["the london reset", "£2,860"],
          ["the countryside switch-off", "£3,240"],
          ["the maker day", "£2,650"],
        ].map(([name, cost]) => (
          <div
            key={name}
            className="flex items-center justify-between rounded-xl border border-border bg-background px-3.5 py-3"
          >
            <span className="font-funnel font-bold text-sm text-foreground truncate">{name}</span>
            <span className="text-xs text-muted-foreground">{cost}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <ol className="space-y-3">
      {[
        ["10:15", "strategy session"],
        ["12:30", "good lunch"],
        ["18:00", "hijingo"],
      ].map(([time, label]) => (
        <li key={time} className="flex items-baseline gap-4">
          <span className="font-funnel text-xs text-muted-foreground w-10">{time}</span>
          <span className="text-foreground">{label}</span>
        </li>
      ))}
    </ol>
  );
}

function AdFrame({
  format,
  concept,
}: {
  format: Format;
  concept: (typeof adConcepts)[number];
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-card overflow-hidden flex flex-col",
        format === "square" ? "aspect-square" : "aspect-[4/5]"
      )}
    >
      <div className="px-6 pt-6 flex items-baseline gap-2">
        <span className="font-funnel font-bold text-lg text-foreground">qwip</span>
        <span className="text-xs text-muted-foreground">offsites</span>
      </div>
      <div className="px-6 pt-5">
        <h3 className="font-funnel font-bold text-xl tablet:text-2xl laptop:text-2xl desktop:text-2xl leading-tight text-foreground">
          {concept.headline}
        </h3>
      </div>
      <div className="flex-1 px-6 py-5 min-h-0 overflow-hidden">
        <Visual kind={concept.visual} />
      </div>
      <div className="px-6 pb-6">
        <p className="text-sm text-muted-foreground mb-4">{concept.support}</p>
        <span className="inline-block rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm">
          {concept.cta}
        </span>
      </div>
    </div>
  );
}

export default function OffsiteAds() {
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    document.title = "qwip offsites ad concepts | internal";
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="container mx-auto px-4 laptop:px-6 desktop:px-6 h-16 flex items-center justify-between">
          <Link
            to="/offsite-demo"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={15} /> back to the demo
          </Link>
          <div className="flex items-baseline gap-2">
            <span className="font-funnel font-bold text-xl text-foreground">qwip</span>
            <span className="text-sm text-muted-foreground">offsites</span>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 laptop:px-6 desktop:px-6 py-14">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">
            internal only
          </p>
          <h1 className="font-funnel font-bold text-3xl tablet:text-4xl laptop:text-5xl desktop:text-5xl text-foreground mb-4">
            paid ad{" "}
            <span className="font-cormorant italic font-normal">concepts.</span>
          </h1>
          <p className="text-muted-foreground text-lg">
            Four directions to test acquisition for qwip offsites. UI mockups, not exported creative.
          </p>
        </div>

        <div className="space-y-16">
          {adConcepts.map((c, i) => (
            <motion.section
              key={c.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
            >
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-6">
                concept {i + 1}
              </p>

              <div className="grid mobile:grid-cols-1 tablet:grid-cols-1 laptop:grid-cols-[1.15fr_1fr] desktop:grid-cols-[1.15fr_1fr] gap-6 items-start">
                <div>
                  <p className="text-xs text-muted-foreground mb-3">linkedin square</p>
                  <AdFrame format="square" concept={c} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-3">instagram 4:5</p>
                  <AdFrame format="portrait" concept={c} />
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-dashed border-border bg-muted/40">
                <button
                  onClick={() => setOpen(open === c.id ? null : c.id)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left"
                >
                  <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                    <Info size={14} /> internal note
                  </span>
                  <ChevronDown
                    size={16}
                    className={cn(
                      "text-muted-foreground transition-transform",
                      open === c.id && "rotate-180"
                    )}
                  />
                </button>
                {open === c.id && (
                  <motion.dl
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="overflow-hidden px-6 pb-6 grid mobile:grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-4 desktop:grid-cols-4 gap-6"
                  >
                    {[
                      ["intended audience", c.note.audience],
                      ["pain being tested", c.note.pain],
                      ["suggested channel", c.note.channel],
                      ["suggested cta", c.note.cta],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt className="text-xs text-muted-foreground mb-1.5">{label}</dt>
                        <dd className="text-sm text-foreground">{value}</dd>
                      </div>
                    ))}
                  </motion.dl>
                )}
              </div>
            </motion.section>
          ))}
        </div>
      </div>

      <footer className="border-t border-border py-10">
        <div className="container mx-auto px-4 laptop:px-6 desktop:px-6 flex mobile:flex-col items-center justify-between gap-3">
          <span className="font-funnel font-bold text-xl text-foreground">qwip</span>
          <p className="text-sm text-muted-foreground">
            Internal concept demo. Nothing here is live creative or live pricing.
          </p>
        </div>
      </footer>
    </div>
  );
}
